/**
 * Casino Constants - Myvoxia-v4
 *
 * Design system colors, labels, and configurations
 */

// ============================================================================
// COLORS
// ============================================================================

export const COLORS = {
  // Brand colors
  primary: '#E2001A',      // Bleu Casino
  primaryDark: '#B0000F',
  primaryLight: '#FFC9CE',

  // Status colors
  success: '#28A745',
  warning: '#FFC107',
  danger: '#DC3545',
  neutral: '#6C757D',

  // Speaker colors
  agent: '#4A90E2',        // Bleu agent
  client: '#E74C3C',       // Rouge client
  silence: '#95A5A6',      // Gris silence
  unknown: '#BDC3C7',

  // Sentiment colors
  positive: '#27AE60',
  negative: '#E74C3C',
  frustrated: '#C0392B',
  urgent: '#F39C12',

  // Background colors
  background: '#F8F9FA',
  surface: '#FFFFFF',
  surfaceHover: '#F1F3F5',

  // Text colors
  textPrimary: '#212529',
  textSecondary: '#6C757D',
  textMuted: '#ADB5BD',

  // Chart colors palette
  chart: [
    '#E2001A', '#28A745', '#FFC107', '#DC3545',
    '#6C757D', '#17A2B8', '#6F42C1', '#E83E8C',
    '#20C997', '#FD7E14',
  ],
} as const;

// ============================================================================
// LABELS (French)
// ============================================================================

export const LABELS = {
  // Speaker labels
  speaker: {
    agent: 'Agent',
    client: 'Client',
    unknown: 'Inconnu',
  },

  // Status labels
  status: {
    PENDING: 'En attente',
    PROCESSING: 'En cours',
    SEPARATING: 'Séparation audio',
    DIARIZING: 'Diarisation',
    TRANSCRIBING: 'Transcription',
    ANALYZING: 'Analyse',
    COMPLETED: 'Terminé',
    FAILED: 'Échec',
  },

  // Alert severity
  severity: {
    low: 'Faible',
    medium: 'Moyen',
    high: 'Élevé',
    critical: 'Critique',
  },

  // Alert status
  alertStatus: {
    ACTIVE: 'Active',
    ACKNOWLEDGED: 'Prise en charge',
    RESOLVED: 'Résolue',
    DISMISSED: 'Ignorée',
  },

  // Procedure categories (legacy + Convivia criteria names)
  procedureCategory: {
    retour: 'Retours',
    livraison: 'Livraison',
    garantie: 'Garantie',
    remboursement: 'Remboursement',
    sav: 'SAV',
    commande: 'Commande',
    produit: 'Produit',
    general: 'Général',
    accueil: 'Accueil',
    questionnement: 'Questionnement',
    reponse: 'Qualité réponse',
    procedures: 'Conformité procédures',
    analyse: 'Analyse situation',
    cloture: 'Clôture',
  },

  // Entity types
  entityType: {
    numero_retour: 'N° Retour',
    numero_commande: 'N° Commande',
    numero_rma: 'N° RMA',
    numero_colis: 'N° Colis',
    nom_produit: 'Produit',
    code_article: 'Code Article',
    magasin: 'Magasin',
    date: 'Date',
    montant: 'Montant',
    telephone: 'Téléphone',
    email: 'Email',
  },

  // Quality criteria
  qualityCriteria: {
    greeting: 'Accueil',
    diagnosis: 'Diagnostic',
    solution: 'Solution',
    procedure: 'Procédure',
    pricing_clarity: 'Clarté tarifs',
    closure: 'Conclusion',
  },

  // Sentiment
  sentiment: {
    positive: 'Positif',
    negative: 'Négatif',
    neutral: 'Neutre',
    frustrated: 'Frustré',
    urgent: 'Urgent',
  },

  // Product availability
  availability: {
    in_stock: 'En stock',
    low_stock: 'Stock faible',
    out_of_stock: 'Rupture',
    on_order: 'En commande',
  },

  // Dashboard titles
  dashboard: {
    title: 'Tableau de bord',
    callsProcessed: 'Appels traités',
    satisfaction: 'Satisfaction',
    avgDuration: 'Durée moyenne',
    complianceScore: 'Score conformité',
    savCases: 'Cas SAV',
    activeAlerts: 'Alertes actives',
    topProducts: 'Applications les plus impactées',
    procedureCompliance: 'Conformité procédures',
    recentAlerts: 'Alertes récentes',
    agentPerformance: 'Performance agents',
  },

  // KPI 1: Agent Quality
  agentQuality: {
    title: 'Qualité Agent',
    subtitle: 'Performance du centre d\'appel',
    overall: 'Score global',
    technical: 'Compétences techniques',
    relational: 'Relationnel',
    customerReaction: 'Réaction client',
    empathy: 'Empathie',
    attitude: 'Attitude',
    listening: 'Écoute',
    positive: 'Positive',
    neutral: 'Neutre',
    negative: 'Négative',
  },

  // KPI 2: Casino Satisfaction
  castoramaSatisfaction: {
    title: 'Satisfaction Groupe Casino',
    subtitle: 'Satisfaction des magasins / clients',
    overall: 'Score global',
    products: 'Produits',
    delivery: 'Livraison',
    pricing: 'Prix',
    store: 'Magasin',
    afterSales: 'SAV',
    satisfied: 'Satisfait',
    neutral: 'Neutre',
    dissatisfied: 'Insatisfait',
    frustrated: 'Frustré',
    loyaltyRisk: 'Risque fidélité',
    issueResolved: 'Problème résolu',
  },

  // KPI 3: Convivia Procedure Compliance
  stonlyCompliance: {
    title: 'Conformité Procédures',
    subtitle: 'Analyse Convivia',
    overall: 'Score global',
    greeting: 'Accueil',
    procedureAdherence: 'Respect procédures',
    empathy: 'Empathie',
    closing: 'Clôture',
    matchedProcedures: 'Procédures matchées',
    proceduresFollowed: 'Procédures suivies',
    proceduresMissed: 'Procédures manquées',
    recommendations: 'Recommandations',
    callsAnalyzed: 'Appels analysés',
  },

  // Widget Builder
  widgets: {
    title: 'Widget Builder',
    myWidgets: 'Mes Widgets',
    marketplace: 'Marketplace',
    create: 'Nouveau Widget',
    edit: 'Modifier Widget',
    delete: 'Supprimer',
    save: 'Enregistrer',
    test: 'Tester',
    install: 'Installer',
    preview: 'Aperçu',
    aiPrompt: 'Décrivez le widget souhaité...',
    aiGenerate: 'Générer avec IA',
    aiGenerating: 'Génération en cours...',
    noWidgets: 'Aucun widget créé',
    noResults: 'Aucun résultat',
    loading: 'Chargement...',
    error: 'Erreur',
    success: 'Succès',
    types: {
      kpi: 'KPI',
      chart: 'Graphique',
      gauge: 'Jauge',
      ranking: 'Classement',
      table: 'Tableau',
      composite: 'Composite',
    },
    chartTypes: {
      line: 'Ligne',
      bar: 'Barres',
      pie: 'Camembert',
      doughnut: 'Anneau',
      radar: 'Radar',
      area: 'Aire',
    },
    categories: {
      performance: 'Performance',
      quality: 'Qualité',
      productivity: 'Productivité',
      satisfaction: 'Satisfaction',
      stonly: 'Conformité Convivia',
      alerts: 'Alertes',
    },
    sizes: {
      small: 'Petit',
      medium: 'Moyen',
      large: 'Grand',
      full: 'Pleine largeur',
    },
  },
} as const;

// ============================================================================
// ICONS
// ============================================================================

export const ICONS = {
  // KPI icons
  calls: '📞',
  satisfaction: '⭐',
  duration: '⏱️',
  compliance: '✅',
  sav: '🔧',
  alerts: '🚨',
  trend_up: '↗️',
  trend_down: '↘️',
  trend_stable: '→',

  // Agent Quality KPI
  agentQuality: '🎧',
  technical: '📋',
  relational: '❤️',
  empathy: '❤️',
  attitude: '😊',
  listening: '👂',
  reactionPositive: '👍',
  reactionNeutral: '😐',
  reactionNegative: '👎',

  // Casino Satisfaction KPI
  castoramaSatisfaction: '🏠',
  products: '📦',
  delivery: '🚚',
  pricing: '💰',
  store: '🏬',
  afterSales: '🛠️',
  satisfied: '😊',
  dissatisfied: '😞',
  frustrated: '😤',
  loyaltyRisk: '⚠️',
  issueResolved: '✓',

  // Convivia Compliance KPI
  stonlyCompliance: '📋',
  procedureMatch: '🎯',
  procedureFollowed: '✓',
  procedureMissed: '✗',
  recommendation: '💡',

  // Status icons
  pending: '⏳',
  processing: '⚙️',
  completed: '✅',
  failed: '❌',

  // Severity icons
  low: '🟢',
  medium: '🟡',
  high: '🟠',
  critical: '🔴',

  // Speaker icons
  agent: '👤',
  client: '👥',

  // Action icons
  play: '▶️',
  pause: '⏸️',
  upload: '📤',
  download: '📥',
  refresh: '🔄',
  search: '🔍',
  filter: '🔽',
  settings: '⚙️',
  info: 'ℹ️',
  warning: '⚠️',
  error: '❗',

  // Category icons
  retour: '↩️',
  livraison: '📦',
  garantie: '🛡️',
  remboursement: '💰',
  sav_icon: '🛠️',
  commande: '🛒',
  produit: '📦',
} as const;

// ============================================================================
// API CONFIGURATION
// ============================================================================

export const API_CONFIG = {
  baseUrl: process.env.NEXT_PUBLIC_API_URL || '/api',
  wsUrl: process.env.NEXT_PUBLIC_WS_URL || '',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
} as const;

// ============================================================================
// DASHBOARD CONFIGURATION
// ============================================================================

export const DASHBOARD_CONFIG = {
  refreshInterval: 60000,  // 1 minute
  alertsMaxItems: 10,
  productsMaxItems: 10,
  agentsMaxItems: 10,
  chartHeight: 300,

  // Default date range
  defaultDateRange: 7,  // days

  // Score thresholds
  scoreThresholds: {
    excellent: 90,
    good: 75,
    average: 60,
    poor: 40,
  },

  // Trend calculation period
  trendPeriod: 7,  // days
} as const;

// ============================================================================
// TYPOLOGY LABELS & ICONS
// ============================================================================

export const TYPOLOGY_LABELS: Record<string, string> = {
  acces_mdp: 'Accès & mot de passe',
  applicatif_gold_sap: 'Applicatif GOLD / SAP',
  bureautique_messagerie: 'Bureautique & messagerie',
  impression_materiel: 'Impression & matériel',
  reseau_connectivite: 'Réseau & connectivité',
  caisse_encaissement: 'Caisse & encaissement',
  monetique: 'Monétique (Worldline)',
  sco_libre_service: 'SCO libre-service',
  balance_etiquetage: 'Balance & étiquetage',
};

export const TYPOLOGY_ICONS: Record<string, string> = {
  acces_mdp: '🔑',
  applicatif_gold_sap: '🧩',
  bureautique_messagerie: '📧',
  impression_materiel: '🖨️',
  reseau_connectivite: '🌐',
  caisse_encaissement: '🧾',
  monetique: '💳',
  sco_libre_service: '🛒',
  balance_etiquetage: '⚖️',
};

export const TYPOLOGY_KEYS = [
  'acces_mdp',
  'applicatif_gold_sap',
  'bureautique_messagerie',
  'impression_materiel',
  'reseau_connectivite',
  'caisse_encaissement',
  'monetique',
  'sco_libre_service',
  'balance_etiquetage',
] as const;

export const TYPOLOGY_COLORS: Record<string, string> = {
  acces_mdp: '#E2001A',
  applicatif_gold_sap: '#B0000F',
  bureautique_messagerie: '#3498DB',
  impression_materiel: '#E67E22',
  reseau_connectivite: '#34495E',
  caisse_encaissement: '#9B59B6',
  monetique: '#1ABC9C',
  sco_libre_service: '#27AE60',
  balance_etiquetage: '#F1C40F',
};

// ============================================================================
// UPLOAD CONFIGURATION
// ============================================================================

export const UPLOAD_CONFIG = {
  maxFileSize: 100 * 1024 * 1024,  // 100 MB
  acceptedFormats: ['.mp3', '.wav', '.m4a'],
  acceptedMimeTypes: ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-m4a'],
  chunkSize: 1024 * 1024,  // 1 MB chunks for large files
} as const;

// ============================================================================
// AUDIO PLAYER CONFIGURATION
// ============================================================================

export const AUDIO_PLAYER_CONFIG = {
  waveformHeight: 80,
  waveformColor: COLORS.primary,
  progressColor: COLORS.primaryDark,
  cursorColor: COLORS.danger,
  playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
  defaultPlaybackRate: 1,
} as const;

// ============================================================================
// MOCK DATA
// ============================================================================

export const MOCK_DATA = {
  globalKPI: {
    callsProcessed: 1245,
    callsTrend: 12,
    satisfaction: 8.7,
    satisfactionTrend: 5,
    avgDuration: 305,
    durationTrend: -8,
    complianceScore: 87.5,
    complianceTrend: 3,
    savCases: 23,
    savTrend: -15,
    activeAlerts: 5,
    alertsTrend: -20,
  },

  topProducts: [
    { name: 'Applicatif GOLD', mentions: 312, availability: 'in_stock' as const },
    { name: 'SAP (commandes / BL)', mentions: 268, availability: 'in_stock' as const },
    { name: 'Caisse Toshiba', mentions: 241, availability: 'low_stock' as const },
    { name: 'Messagerie Outlook', mentions: 198, availability: 'in_stock' as const },
    { name: 'TPE Worldline', mentions: 156, availability: 'in_stock' as const },
    { name: 'Active Directory (MDP)', mentions: 145, availability: 'in_stock' as const },
    { name: 'Imprimante / étiquettes', mentions: 121, availability: 'low_stock' as const },
    { name: 'Serveur caisses Storemate', mentions: 98, availability: 'on_order' as const },
    { name: 'PDA / terminal mobile', mentions: 87, availability: 'in_stock' as const },
    { name: 'Caisse libre-service (SCO)', mentions: 76, availability: 'out_of_stock' as const },
  ],

  procedureCompliance: {
    retour: { score: 78, trend: -2, total: 234 },
    livraison: { score: 91, trend: 5, total: 456 },
    garantie: { score: 85, trend: 0, total: 123 },
    remboursement: { score: 82, trend: 3, total: 89 },
    sav: { score: 85, trend: 1, total: 178 },
    commande: { score: 89, trend: 4, total: 345 },
  },

  recentAlerts: [
    {
      id: 'alert_001',
      type: 'frustrated_client',
      severity: 'critical' as const,
      title: 'Client frustré détecté',
      message: 'Appel CALL_20260122_045 - Frustration détectée sur retour produit',
      timestamp: '2026-01-22T10:45:00Z',
      status: 'ACTIVE' as const,
      audioFileId: 'audio_001',
    },
    {
      id: 'alert_002',
      type: 'low_score',
      severity: 'high' as const,
      title: 'Score qualité bas',
      message: 'Agent Martin - Score 42/100 sur dernier appel',
      timestamp: '2026-01-22T10:30:00Z',
      status: 'ACKNOWLEDGED' as const,
      audioFileId: 'audio_002',
    },
    {
      id: 'alert_003',
      type: 'sav_required',
      severity: 'medium' as const,
      title: 'Intervention SAV requise',
      message: 'RMA demandé pour chaudière - Client prioritaire',
      timestamp: '2026-01-22T10:15:00Z',
      status: 'ACTIVE' as const,
      audioFileId: 'audio_003',
    },
    {
      id: 'alert_004',
      type: 'long_call',
      severity: 'low' as const,
      title: 'Appel long détecté',
      message: 'Durée: 23min - Sujet: réclamation livraison',
      timestamp: '2026-01-22T09:45:00Z',
      status: 'RESOLVED' as const,
      audioFileId: 'audio_004',
    },
  ],

  agentPerformance: [
    { id: 'agent_001', name: 'Sophie Martin', calls: 156, avgScore: 92, satisfaction: 9.2, avgDuration: 245 },
    { id: 'agent_002', name: 'Thomas Dupont', calls: 143, avgScore: 88, satisfaction: 8.8, avgDuration: 290 },
    { id: 'agent_003', name: 'Marie Lambert', calls: 138, avgScore: 85, satisfaction: 8.5, avgDuration: 310 },
    { id: 'agent_004', name: 'Pierre Durand', calls: 128, avgScore: 79, satisfaction: 7.9, avgDuration: 340 },
    { id: 'agent_005', name: 'Julie Bernard', calls: 122, avgScore: 91, satisfaction: 9.0, avgDuration: 260 },
  ],

  savCases: [
    {
      id: 'sav_001',
      rmaNumber: 'RMA2026012201',
      product: 'Chaudière Elm Leblanc',
      status: 'pending' as const,
      priority: 'high' as const,
      createdAt: '2026-01-22T08:00:00Z',
      clientName: 'M. Dubois',
    },
    {
      id: 'sav_002',
      rmaNumber: 'RMA2026012202',
      product: 'Climatiseur Daikin',
      status: 'in_progress' as const,
      priority: 'medium' as const,
      createdAt: '2026-01-21T14:30:00Z',
      clientName: 'Mme Garcia',
    },
    {
      id: 'sav_003',
      rmaNumber: 'RMA2026012203',
      product: 'Lave-vaisselle Bosch',
      status: 'scheduled' as const,
      priority: 'low' as const,
      createdAt: '2026-01-21T10:15:00Z',
      clientName: 'M. Petit',
    },
  ],
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function getScoreColor(score: number): string {
  if (score >= DASHBOARD_CONFIG.scoreThresholds.excellent) return COLORS.success;
  if (score >= DASHBOARD_CONFIG.scoreThresholds.good) return COLORS.primary;
  if (score >= DASHBOARD_CONFIG.scoreThresholds.average) return COLORS.warning;
  return COLORS.danger;
}

export function getSeverityColor(severity: string): string {
  const colorMap: Record<string, string> = {
    low: COLORS.success,
    medium: COLORS.warning,
    high: '#F39C12',
    critical: COLORS.danger,
    LOW: COLORS.success,
    MEDIUM: COLORS.warning,
    HIGH: '#F39C12',
    CRITICAL: COLORS.danger,
  };
  return colorMap[severity] || COLORS.neutral;
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatTrend(value: number): { text: string; icon: string; color: string } {
  if (value > 0) {
    return { text: `+${value}%`, icon: ICONS.trend_up, color: COLORS.success };
  }
  if (value < 0) {
    return { text: `${value}%`, icon: ICONS.trend_down, color: COLORS.danger };
  }
  return { text: '0%', icon: ICONS.trend_stable, color: COLORS.neutral };
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '0';
  return value.toLocaleString('fr-FR');
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTimeAgo(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const now = new Date();
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return formatDate(date);
}
